
import { Component, EventEmitter, inject, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AlertPopup, config, HttpLoader } from "app/app.config";
import { ChatService } from "app/components/chat-list/chat.service";
import { ELibraryService } from "app/components/elibraryandcourse/e-library/e-library.service";
import { ConfirmationDialogService } from "app/confirmation-dialog/confirmation-dialog.service";

interface WorkflowStep {
  title: string;
  description: string;
  icon: string;
}

export interface AssessmentOption {
  text: string;
  isCorrect: boolean;
}

export interface AssessmentQuestion {
  question: string;
  type: 'single' | 'multiple';
  marks: number;
  options: AssessmentOption[];
}

export interface AssessmentSettings {
  allowRetest: boolean;
  maximumMarks: number;
  passPercentage: number;
}

//Survey
interface SurveyQuestion {
  question: string;
  choices: string[];
}

//Poll
interface PollQuestion {
  question: string;
  choices: string[];
}


@Component({
  selector: "document-upload",
  standalone: false,
  templateUrl: "./document-upload.component.html",
  styleUrl: "./document-upload.component.scss",
})
export class DocumentUploadComponent implements OnInit {

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private httpLoader = inject(HttpLoader);
  private alert = inject(AlertPopup);
  private confirmDialogService = inject(ConfirmationDialogService);
  private elibraryService = inject(ELibraryService);
  private chatService = inject(ChatService)

  currentStep = 0;

  selectedFile: File | null = null;

  upLoadedFileList: File[] = [];

  uploadProgress = 0;

  isUploading = false;

  showEditor = false;

  showExcelEditor = false;

  showContinueDialog = false;

  documentContent = '';

  noteText = '';

  documentTitle = '';

  description = '';

  selectedPolicy = '';

  isDownload = true;

  loading: boolean = false;

  errorMessage: string = '';

  selectedFolderId: any = '';

  companyIdForConversion: any = '';

  message_id: any = '';

  selectedClassList: any[] = [];

  activeFolderModelType: any = '';

  offset = config.DEFAULT_OFFSET;
  limit = config.DEFAULT_RECORD_LIMIT;

  documentId: any = '';
  documentFileName: any = '';
  documentFileType: any = "";

  //Assessment
  assessmentMode: 'manual' | 'ai' = 'manual';

  assessmentSaved = false;

  assessmentSettings: AssessmentSettings = {
    allowRetest: false,
    maximumMarks: 100,
    passPercentage: 60
  };

  questions: AssessmentQuestion[] = [
    {
      question: '',
      type: 'single',
      marks: 1,
      options: [
        {
          text: '',
          isCorrect: false
        }
      ]
    }
  ];

  //Survey
  surveyStarted = false;

  surveyQuestions: SurveyQuestion[] = [
    {
      question: '',
      choices: ['', '']
    }
  ];

  //Poll
  showPollBuilder = false;

  pollQuestions: PollQuestion[] = [];


  policies = [
    {
      id: 'general',
      name: 'General Policy'
    },
    {
      id: 'hr',
      name: 'HR Policy'
    },
    {
      id: 'compliance',
      name: 'Compliance Policy'
    },
    {
      id: 'training',
      name: 'Training Policy'
    }
  ];

  stateNames: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming'
  };


  steps: WorkflowStep[] = [
    {
      title: 'Upload',
      description: 'Upload & edit document',
      icon: 'pi pi-upload'
    },
    {
      title: 'Assessment',
      description: 'Create questions',
      icon: 'pi pi-list-check'
    },
    {
      title: 'Survey',
      description: 'Collect feedback',
      icon: 'pi pi-comments'
    },
    {
      title: 'Poll',
      description: 'Create a poll',
      icon: 'pi pi-chart-bar'
    },
    {
      title: 'Notes',
      description: 'Add notes',
      icon: 'pi pi-bookmark'
    }
  ];

  ngOnInit(): void {

    const groupId = this.route.snapshot.paramMap.get('groupId');

    if (!groupId) {
      this.alert.showAlertMessage('Group ID is missing.');
      return;
    }

    this.selectedFolderId = groupId;

    const companyId = localStorage.getItem('company_id');

    this.companyIdForConversion = companyId
      ? Number(companyId)
      : null;

    const payload = {
      offset: this.offset,
      limit: 1000,
      groupId: this.selectedFolderId
    };

    this.chatService.getGroupMemberList(payload).subscribe({

      next: (response: any) => {

        const userList = response?.groupMemberList ?? [];
        const groupDetail = response?.group_detail;

        this.activeFolderModelType =
          groupDetail?.model_type ?? '';

        this.selectedClassList = userList
          .map((user: any) => user.user_id)
          .filter((id: any) => id != null);
      },

      error: (error: any) => {

        console.error(
          'Failed to load group members:',
          error
        );

        this.selectedClassList = [];
      }

    });
  }


  // =====================================================
  // FILE
  // =====================================================

  onFileSelected(event: Event): void {

    const input = event.target as HTMLInputElement;

    if (!input.files || input.files.length === 0) {
      return;
    }

    this.selectedFile = input.files[0];

    this.uploadProgress = 0;
  }


  removeFile(event: Event): void {

    event.stopPropagation();

    this.selectedFile = null;

    this.uploadProgress = 0;
  }


  formatFileSize(size: number): string {

    if (size < 1024) {
      return `${size} B`;
    }

    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }


  // =====================================================
  // UPLOAD → EDITOR
  // =====================================================

  continueToEditor(): void {

    if (!this.selectedFile) {
      return;
    }

    this.upLoadedFileList = [
      this.selectedFile
    ];

    // New document mode
    this.documentId = null;

    this.documentFileName =
      this.selectedFile.name;

    this.documentFileType =
      this.selectedFile.name
        .split('.')
        .pop() || '';

    this.errorMessage = '';

    this.showEditor = true;

    this.showExcelEditor = true;

    this.showContinueDialog = false;
  }

  // =====================================================
  // EDITOR
  // =====================================================


  onExcelEditorSave(event: any): void {

    console.log(
      'Excel editor saved:',
      event
    );

    this.showExcelEditor = false;

    this.showEditor = false;

    this.showContinueDialog = true;
  }

  onExcelEditorCancel(): void {

    this.showExcelEditor = false;

    this.showEditor = false;

    this.errorMessage = '';
  }

  closeEditor(): void {

    this.showExcelEditor = false;

    this.showEditor = false;

    this.errorMessage = '';
  }


  saveDocument(): void {

    /*
     * Call your save/update document API here.
     *
     * Example:
     *
     * this.documentService.updateDocument({
     *   content: this.documentContent
     * })
     */

    this.showEditor = false;

    this.showContinueDialog = true;
  }


  // =====================================================
  // SAVE ONLY
  // =====================================================

  saveOnly(): void {
    this.showContinueDialog = false;
    this.currentStep = 0;

    this.router.navigate([
      '/documentnew',
      this.selectedFolderId
    ]);
  }


  // =====================================================
  // YES → NEXT STEP
  // =====================================================

  continueNextStep(): void {

    this.showContinueDialog = false;

    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
    }
  }


  // =====================================================
  // OTHER STEPS
  // =====================================================

  completeStep(): void {

    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
    }
  }


  previousStep(): void {

    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }


  // =====================================================
  // FINAL SAVE
  // =====================================================

  saveWorkflow(): void {

    /*
     * Save assessment / survey / poll / notes.
     */

    console.log({
      document: this.documentContent,
      note: this.noteText
    });

    alert('Workflow completed successfully.');
  }


  goBack(): void {

    window.history.back();
  }

  editDocument(document: any): void {

    this.documentId = document.message_id;

    this.documentFileName =
      document.file_name ||
      document.original_file ||
      '';

    this.documentFileType =
      document.file_type ||
      this.documentFileName.split('.').pop() ||
      '';

    this.documentTitle =
      document.title ||
      '';

    this.upLoadedFileList = [];

    this.showEditor = true;
    this.showExcelEditor = true;
    this.showContinueDialog = false;
  }

  //==================================================
  // Assement stepper
  //==================================================

  addQuestion(): void {
    this.questions.push({
      question: '',
      type: 'single',
      marks: 1,
      options: [
        { text: '', isCorrect: false }
      ]
    });
  }

  addOption(question: AssessmentQuestion): void {
    question.options.push({
      text: '',
      isCorrect: false
    });
  }

  removeOption(
    question: AssessmentQuestion,
    index: number
  ): void {

    if (question.options.length <= 2) {
      return;
    }

    const removedCorrect =
      question.options[index].isCorrect;

    question.options.splice(index, 1);

    if (
      removedCorrect &&
      !question.options.some(x => x.isCorrect)
    ) {
      question.options[0].isCorrect = true;
    }
  }

  removeQuestion(index: number): void {

    if (this.questions.length <= 1) {
      return;
    }

    this.questions.splice(index, 1);
  }

  selectCorrectAnswer(
    question: AssessmentQuestion,
    index: number
  ): void {

    if (question.type === 'single') {

      question.options.forEach(
        (option, i) => {
          option.isCorrect = i === index;
        }
      );

    } else {

      question.options[index].isCorrect =
        !question.options[index].isCorrect;

    }
  }

  getOptionLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  getTotalQuestionMarks(): number {

    return this.questions.reduce(
      (total, question) =>
        total + Number(question.marks || 0),
      0
    );
  }

  //Survey configuration
  startSurvey(): void {
    this.surveyStarted = true;

    if (!this.surveyQuestions.length) {
      this.surveyQuestions = [
        {
          question: '',
          choices: ['', '']
        }
      ];
    }
  }

  addSurveyQuestion(): void {
    this.surveyQuestions.push({
      question: '',
      choices: ['', '']
    });
  }

  removeSurveyQuestion(index: number): void {
    if (this.surveyQuestions.length <= 1) {
      return;
    }

    this.surveyQuestions.splice(index, 1);
  }

  addSurveyChoice(questionIndex: number): void {
    this.surveyQuestions[questionIndex].choices.push('');
  }

  removeSurveyChoice(
    questionIndex: number,
    choiceIndex: number
  ): void {

    const choices =
      this.surveyQuestions[questionIndex].choices;

    if (choices.length <= 1) {
      return;
    }

    choices.splice(choiceIndex, 1);
  }


  saveSurvey(): void {

    const validQuestions = this.surveyQuestions.filter(
      question =>
        question.question.trim() &&
        question.choices.some(choice => choice.trim())
    );

    console.log('Survey:', validQuestions);

    // Call your API here
  }

  continueToPoll(): void {

    const isValid = this.validateSurvey();

    if (!isValid) {
      return;
    }

    // Move to next step
    this.currentStep = 3;
  }

  validateSurvey(): boolean {

    for (const question of this.surveyQuestions) {

      if (!question.question.trim()) {
        return false;
      }

      const validChoices = question.choices.filter(
        choice => choice.trim()
      );

      if (validChoices.length < 2) {
        return false;
      }
    }

    return true;
  }

  /* =====================================================
   POLL
===================================================== */

  addNewPoll(): void {
    this.showPollBuilder = true;

    if (this.pollQuestions.length === 0) {
      this.pollQuestions.push({
        question: '',
        choices: ['', '']
      });
    }
  }


  /* =====================================================
     ADD QUESTION
  ===================================================== */

  addPollQuestion(): void {
    this.pollQuestions.push({
      question: '',
      choices: ['', '']
    });
  }


  /* =====================================================
     REMOVE QUESTION
  ===================================================== */

  removePollQuestion(index: number): void {

    if (this.pollQuestions.length <= 1) {
      return;
    }

    this.pollQuestions.splice(index, 1);
  }


  /* =====================================================
     ADD CHOICE
  ===================================================== */

  addPollChoice(questionIndex: number): void {

    this.pollQuestions[questionIndex].choices.push('');
  }


  /* =====================================================
     REMOVE CHOICE
  ===================================================== */

  removePollChoice(
    questionIndex: number,
    choiceIndex: number
  ): void {

    const choices =
      this.pollQuestions[questionIndex].choices;

    // Keep minimum two choices
    if (choices.length <= 2) {
      return;
    }

    choices.splice(choiceIndex, 1);
  }


  /* =====================================================
     CHOICE LETTER
  ===================================================== */

  getChoiceLetter(index: number): string {

    return String.fromCharCode(65 + index);
  }


  /* =====================================================
     SAVE POLL
  ===================================================== */

  savePoll(): void {

    const isValid = this.validatePoll();

    if (!isValid) {
      return;
    }

    console.log('Poll saved:', this.pollQuestions);

    // API call can be added here
  }


  /* =====================================================
     VALIDATE POLL
  ===================================================== */

  validatePoll(): boolean {

    for (
      const question of this.pollQuestions
    ) {

      if (!question.question.trim()) {
        alert('Please enter the poll question.');
        return false;
      }

      if (question.choices.length < 2) {
        alert('Each question must have at least two choices.');
        return false;
      }

      const hasEmptyChoice =
        question.choices.some(
          choice => !choice.trim()
        );

      if (hasEmptyChoice) {
        alert('Please enter all choices.');
        return false;
      }
    }

    return true;
  }


  /* =====================================================
     CONTINUE TO NOTE
  ===================================================== */

  continueToNote(): void {

    if (!this.validatePoll()) {
      return;
    }

    this.savePoll();

    // Step 5 - Note
    this.currentStep = 4;
  }
}
